import { useState } from 'react';
import type { BuildingType, BuildingZone, Planet, PlayerRace, TickCheckpointEventOf } from '@kaeliux/shared';
import { BUILDING_DEFINITIONS } from '@kaeliux/shared';
import { getBuildingIcon } from '../../utils/buildingIcons';
import { usePlanetView } from '../../contexts/PlanetViewContext';
import type { PlanetActionOptions } from '../../services/planet.service';
import { ZONE_LABEL } from './zoneStyles';
import { CostChips } from './CostChips';
import { BuiltPlotContent } from './BuiltPlotContent';

export interface PlotChooserContentProps {
    zone: BuildingZone;
    /** The tapped plot's id (DS-28) — passed straight through to whichever building the player picks. */
    plotId: string;
    planet: Planet;
    race: PlayerRace | undefined;
    scienceLevels: Partial<Record<string, number>>;
    projectedLevels: Partial<Record<BuildingType, number>>;
    pendingBuildsByBuilding: Partial<Record<BuildingType, TickCheckpointEventOf<'buildingBuild'>[]>>;
    queueFull: boolean;
    options: PlanetActionOptions;
    now: number;
    onClose: () => void;
}

const LIST_CLASSES = 'flex flex-col gap-2';

const INTRO_CLASSES = 'text-body text-content-muted';

const ROW_CLASSES =
    'flex w-full items-center justify-between gap-2 rounded-md border border-[color:var(--zone-accent)]/40 px-2 py-1.5 text-left transition-colors hover:bg-space-800/60';

const ROW_ICON_CLASSES = 'rounded-md border border-[color:var(--zone-accent)] bg-space-950 object-cover';

const ROW_ICON_SIZE = { width: 32, height: 40 };

const ROW_NAME_CLASSES = 'text-body font-semibold text-content-primary';

const EMPTY_MESSAGE_CLASSES = 'text-body text-content-muted';

const BACK_BUTTON_CLASSES = 'text-label text-content-muted underline';

/**
 * `BuildingPopover`'s content for an empty plot (DS-28) — a chooser of the
 * tapped plot's zone's unbuilt buildings, any of which can go on THIS
 * plot's own free placement replaced DS-25's fixed building↔position
 * binding, so a plot no longer decides in advance which building it can
 * ever hold. Picking one drills into `BuiltPlotContent` (already wired to
 * `usePlanetBuildActions` and already rendering `Construct` at level 0),
 * passing this plot's id along so the server places the construct exactly
 * where the player tapped.
 */
export function PlotChooserContent({
    zone,
    plotId,
    planet,
    race,
    scienceLevels,
    projectedLevels,
    pendingBuildsByBuilding,
    queueFull,
    options,
    now,
    onClose,
}: PlotChooserContentProps) {
    const [selected, setSelected] = useState<BuildingType | null>(null);
    const view = usePlanetView();

    const unbuiltTypes = (Object.keys(BUILDING_DEFINITIONS) as BuildingType[]).filter(
        (type) => BUILDING_DEFINITIONS[type].zone === zone && (planet.buildings[type]?.level ?? 0) === 0,
    );

    if (selected) {
        return (
            <div className={LIST_CLASSES}>
                <button type="button" className={BACK_BUTTON_CLASSES} data-testid="plot-chooser-back" onClick={() => setSelected(null)}>
                    ← Back to list
                </button>
                <BuiltPlotContent
                    buildingType={selected}
                    zone={zone}
                    plotId={plotId}
                    planet={planet}
                    race={race}
                    scienceLevels={scienceLevels}
                    projectedLevel={projectedLevels[selected] ?? 0}
                    queuedEvents={pendingBuildsByBuilding[selected] ?? []}
                    queueFull={queueFull}
                    options={options}
                    now={now}
                    onClose={onClose}
                />
            </div>
        );
    }

    if (unbuiltTypes.length === 0) {
        return <p className={EMPTY_MESSAGE_CLASSES}>Every building in the {ZONE_LABEL[zone]} zone is already built.</p>;
    }

    return (
        <div className={LIST_CLASSES} data-testid="plot-chooser">
            <p className={INTRO_CLASSES}>Choose a building for the {ZONE_LABEL[zone]} zone.</p>
            {unbuiltTypes.map((buildingType) => {
                const def = BUILDING_DEFINITIONS[buildingType];
                return (
                    <button
                        key={buildingType}
                        type="button"
                        className={ROW_CLASSES}
                        data-testid={`plot-chooser-option-${buildingType}`}
                        onClick={() => setSelected(buildingType)}
                    >
                        <span className="flex items-center gap-2">
                            <img
                                src={getBuildingIcon(buildingType, 0, view.buildingRace(buildingType) ?? race)}
                                alt=""
                                width={ROW_ICON_SIZE.width}
                                height={ROW_ICON_SIZE.height}
                                className={ROW_ICON_CLASSES}
                            />
                            <span className={ROW_NAME_CLASSES}>{def.name}</span>
                        </span>
                        <CostChips
                            cost={planet.buildings[buildingType]?.nextCost ?? def.baseCost}
                            testIdPrefix={`plot-chooser-cost-${buildingType}`}
                        />
                    </button>
                );
            })}
        </div>
    );
}

export default PlotChooserContent;
